import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { switchMap, map, catchError, tap } from 'rxjs/operators';
import { of, from } from 'rxjs';
import forEach from 'lodash/forEach';
import { RequestParams } from '../../types';
import { v4 as randomUUIDGenerator } from 'uuid';
import { FEATURE_FLAGS } from '../../constants/launch-darkly-feature-flags.constants';
import { LDClientService } from '../../services/ldclient.service';
import { WkAppConfigService } from '../../services/wk-app-config.service';
import { WkAlfrescoApiService } from '../../services/wk-alfresco-api.service';
import {
  SetFeatureFlags,
  GetFeatureFlags,
  GET_FEATURE_FLAGS,
} from '../actions/app.custom-actions';

@Injectable()
export class FeatureFlagsEffects {
  private lastLoginSite: string;
  constructor(
    private readonly actions$: Actions,
    private readonly ldClientService: LDClientService,
    private readonly configService: WkAppConfigService,
    private readonly alfrescoApiService: WkAlfrescoApiService
  ) {}

  GetFeatureFlags$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetFeatureFlags>(GET_FEATURE_FLAGS),
      switchMap((action: GetFeatureFlags) => {
        const { person, groups } = action.payload;

        this.ldClientService.ldcInitializing(true);
        const getPreferencesRequest: RequestParams = {
          path: 'alfresco/s/api/people/{id}/preferences',
          httpMethod: 'GET',
          pathParams: { id: person.id },
          contentTypes: ['application/json'],
          accepts: ['application/json'],
        };

        return from(
          this.alfrescoApiService.callContentClientCustomApi(
            getPreferencesRequest
          )
        ).pipe(
          switchMap((preferences) => {
            let key = preferences['ldcContextKey'];
            this.lastLoginSite = preferences.lastLogInSite;
            if (key) {
              return of(key);
            } else {
              key = randomUUIDGenerator();
              const setPreferencesRequest: RequestParams = {
                path: 'alfresco/s/api/people/{id}/preferences',
                httpMethod: 'POST',
                pathParams: { id: person.id },
                bodyParam: { ldcContextKey: key },
                contentTypes: ['application/json'],
                accepts: ['application/json'],
              };
              return from(
                this.alfrescoApiService.callContentClientCustomApi(
                  setPreferencesRequest
                )
              ).pipe(map(() => key));
            }
          }),
          switchMap((key: string) => {
            const currentSite =
              localStorage.getItem('currentSite') ?? this.lastLoginSite;
            const envKey = this.configService.get<string>(
              'ldcInfo.environmentKey'
            );
            const tenantKey =
              this.configService.get<string>('ldcInfo.tenantKey');
            const context = {
              kind: 'multi',
              user: {
                name: `${person.firstName} ${person.lastName}`,
                key,
                email: person.id ?? person.email,
                groups,
              },
              organization: {
                key: tenantKey,
                name: `${envKey}_${tenantKey}`,
                siteId: currentSite,
                email: person.id ?? person.email,
              },
            };

            return from(this.ldClientService.identify(context)).pipe(
              switchMap(() => {
                const flags: Record<string, boolean> = {};
                forEach(Object.keys(FEATURE_FLAGS), (flagKey) => {
                  flags[FEATURE_FLAGS[flagKey]] =
                    this.ldClientService.variation<boolean>(
                      FEATURE_FLAGS[flagKey],
                      false
                    );
                });
                return of(new SetFeatureFlags({ flags }));
              })
            );
          }),
          catchError((error) => {
            console.error('Error loading feature flags:', error);
            return of(new SetFeatureFlags({ flags: {} }));
          }),
          tap(() => this.ldClientService.ldcInitializing(false))
        );
      })
    )
  );
}
